import {
	CMIC_TREE_NODE_TYPES,
	CMIC_FIELD_REST__NAMES,
	CMIC_OBJECT_CODES,
	ACTION_ROLE,
} from './../../../shared/Constants';
import { MessageBarType } from '@fluentui/react';
import { v4 as uuidv4 } from 'uuid';
import { downloadAttachmentLocally } from './../SaveAttachment';
import axios from 'axios';
import { filterEmptyFields, hashFileName } from '../../../utility/UtilityFunctions';
import { segmentDefaultTrackCall } from '../../../utility/segment/segmentUtility';

export const handleUploadAttsToExistingPMItems = async (
	PMData,
	attachments,
	userInfo,
	setShowToast,
	setIsLoading,
	selectedProject,
	selectedImagingDoc,
) => {
	const failedAttachments = [];
	const segmentAttachmentsObj = [];
	const isExistingImagingFileUpload =
		PMData?.ecmParentNodeType === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_REVISION &&
		!!(PMData?.ImgdVUuid || PMData?.nodeId || PMData?.VUuid);

	if (attachments.length > 0) {
		for (const mailData of attachments) {
			let index = 0;

			try {
				if (isExistingImagingFileUpload) {
					const documentUuid = PMData.ImgdVUuid || PMData.nodeId || PMData.VUuid;
					const imagingSourceId = selectedImagingDoc?.value || '';
					const docTypeId = PMData?.ImgdtVUuid || '';
					const uploadResponse = await uploadRevisionToExistingImagingDocument(documentUuid, imagingSourceId, docTypeId, mailData, userInfo);

					if (!uploadResponse) {
						segmentAttachmentsObj.push(addSegmentObj(mailData, 'fail'));
						throw new Error('Document upload to existing imaging file failed!');
					}

					segmentAttachmentsObj.push(addSegmentObj(mailData, 'success'));
					index++;
					continue;
				}

				const recordData = await createPMDocumentRecord(PMData, { ...mailData, index }, userInfo, setIsLoading);

				if (!recordData) {
					segmentAttachmentsObj.push(addSegmentObj(mailData, 'fail'));
					throw new Error('Record creation failed!');
				}

				const uploadResponse = await uploadDocumentsToRecord(recordData, mailData, userInfo, setIsLoading);

				if (uploadResponse.message === 'Document File Upload Success') {
					const result = await attachUploadedDocumentToPMObject(
						PMData,
						recordData,
						mailData,
						userInfo,
						setIsLoading,
					);

					if (!result) {
						segmentAttachmentsObj.push(addSegmentObj(mailData, 'fail'));
						throw new Error('Document attachment failed!');
					} else segmentAttachmentsObj.push(addSegmentObj(mailData, 'success'));
				} else {
					segmentAttachmentsObj.push(addSegmentObj(mailData, 'fail'));
					throw new Error('Document upload to record failed!');
				}
			} catch (error) {
				console.log('handleUploadAttsToExistingPMItems() ERROR', error);
				failedAttachments.push(mailData);
			}
			index++;
		}

		//Segment TRACK call triggered when the user uploads an attachment to an existing RFI / Issue

		if (userInfo.actionRole === ACTION_ROLE.PROJECT && userInfo?.isCloudUser) {
			if (segmentAttachmentsObj.length > 0) {
				let segmentObj = {
					contactCode: userInfo?.info?.contactCode,
					timeStamp: new Date(),
					projectName: selectedProject?.PmpProjName,
					projectId: selectedProject?.PmpVUuid,
					attachments: segmentAttachmentsObj,
				};

				let eventName = 'EXISTING_RFI_FILE_UPLOAD';

				if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_RFI) {
					segmentObj.RFIId = PMData.PmscrfiRfiId;
				} else if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE) {
					segmentObj.issueId = PMData?.DmiIssueId;
					eventName = 'EXISTING_ISSUE_FILE_UPLOAD';
				} else if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL) {
					segmentObj.journalId = PMData?.PmjJournalId;
					eventName = 'EXISTING_JOURNAL_FILE_UPLOAD';
				} else if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS) {
					segmentObj.submittalId = PMData?.PmsmSbmtId;
					eventName = 'EXISTING_SUBMITTAL_FILE_UPLOAD';
				}
				segmentDefaultTrackCall(eventName, segmentObj);
			}
		}

		// show Success or failure upload Attachment name
		if (failedAttachments.length > 0) {
			if (failedAttachments.length === 1) {
				setShowToast({
					isVisible: true,
					msg: `Attachment failed to upload: - ${failedAttachments[0]?.name || '-'}`,
					type: MessageBarType.error,
				});
			} else if (failedAttachments.length > 1) {
				let failedAttachmentNames = '';

				failedAttachments.forEach((element, index) => {
					if (index === failedAttachments.length - 1) {
						failedAttachmentNames = `${failedAttachmentNames} ${element.name}`;
					} else {
						failedAttachmentNames = `${failedAttachmentNames} ${element.name}, `;
					}
				});
				setShowToast({
					isVisible: true,
					msg: `Attachments failed to upload: - ${failedAttachmentNames}`,
					type: MessageBarType.error,
				});
			}

			return {
				failedAttachmentsCount: failedAttachments.length,
				uploadedAttachmentsCount: attachments.length - failedAttachments.length,
			};
		} else {
			setShowToast({
				isVisible: true,
				msg: 'Upload Success',
				type: MessageBarType.success,
			});

			return { failedAttachmentsCount: 0, uploadedAttachmentsCount: attachments.length };
		}
	} else {
		return { failedAttachmentsCount: 0, uploadedAttachmentsCount: 0 };
	}
};

const addSegmentObj = (mailData, status) => {
	return {
		name: mailData?.name,
		status: status,
		id: mailData?.id,
	};
};

const uploadRevisionToExistingImagingDocument = async (documentUuid, imagingSourceId, docTypeId, mailData, userInfo) => {
	if (!documentUuid || !imagingSourceId || !docTypeId) {
		console.log('uploadRevisionToExistingImagingDocument() missing params', { documentUuid, imagingSourceId, docTypeId });
		return false;
	}

	const data = new FormData();
	const blobData = downloadAttachmentLocally(mailData);
	data.append('document', blobData, mailData.name);

	const headers = {};

	if (userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth) {
		headers.Authorization = `Bearer ${userInfo?.accessToken}`;
	}

	const config = {
		method: 'post',
		maxBodyLength: Infinity,
		url: `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.uploadDocument}/${imagingSourceId}/${docTypeId}?revision=Y&documentUuid=${encodeURIComponent(documentUuid)}`,
		...(userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth
			? {}
			: {
				auth: {
					username: userInfo.username,
					password: userInfo.password,
				},
			}),
		headers,
		data,
	};

	try {
		const result = await axios.request(config);
		return !!result;
	} catch (error) {
		console.log('uploadRevisionToExistingImagingDocument() Error', error);
		return false;
	}
};

export const handleUploadAttachmentsToExistingPMItems = async (
	PMData,
	attachmentData,
	userInfo,
	setShowToast,
	setIsLoading,
	setAttachmentData,
) => {
	let index = 0;
	let failedAttachments = [];

	try {
		if (attachmentData.length) {
			for (const mailData of attachmentData) {
				const recordData = await createPMDocumentRecord(PMData, { ...mailData, index }, userInfo);

				const uploadResponse = await uploadDocumentsToRecord(recordData, mailData, userInfo);
				if (uploadResponse.message === 'Document File Upload Success') {
					const result = await attachUploadedDocumentToPMObject(
						PMData,
						recordData,
						mailData,
						userInfo,
						setIsLoading,
						setAttachmentData,
					);
				}

				index++;
			}

			// show Success or failure upload Attachment name
			setAttachmentData(prev => {
				let checkFlagUploadAttachment = false;
				prev.forEach(data => {
					if (data.isUploaded !== true) {
						checkFlagUploadAttachment = true;
						failedAttachments.push(data);
						return data;
					}
				});
				if (checkFlagUploadAttachment) {
					let failedAttachmentNames = '';
					if (failedAttachments.length === 1) {
						failedAttachments.forEach(element => {
							failedAttachmentNames = `${failedAttachmentNames} ${element.name}`;
						});
						setShowToast({
							isVisible: true,
							msg: `Attachment failed to upload: - ${failedAttachmentNames}`,
							type: MessageBarType.error,
						});
					} else if (failedAttachments.length > 1) {
						failedAttachments.forEach((element, index) => {
							if (index === failedAttachments.length - 1) {
								failedAttachmentNames = `${failedAttachmentNames} ${element.name}`;
							} else {
								failedAttachmentNames = `${failedAttachmentNames} ${element.name}, `;
							}
						});
						setShowToast({
							isVisible: true,
							msg: `Attachments failed to upload: - ${failedAttachmentNames}`,
							type: MessageBarType.error,
						});
					}
				} else {
					setShowToast({
						isVisible: true,
						msg: 'Upload Success',
						type: MessageBarType.success,
					});
				}
				return prev;
			});
		} else {
			setShowToast({
				isVisible: true,
				msg: 'Select atleast one documents for upload',
				type: MessageBarType.warning,
			});
		}
		return {
			failedAttachmentsCount: failedAttachments.length || 0,
			uploadedAttachmentsCount: attachmentData.length - failedAttachments.length,
		};
	} catch (err) {
		setIsLoading(false);
		console.log('handleUploadAttachmentsToExistingPMItems() ERROR', err);
		setShowToast({
			isVisible: true,
			msg: 'Upload Failed',
			type: MessageBarType.error,
		});
		return { failedAttachmentsCount: 0, uploadedAttachmentsCount: 0 };
	}
};

export const createPMDocumentRecord = (PMData, mailData, userInfo, selectedProject) => {
	return new Promise(resolve => {
		let data = '';
		const uuid = uuidv4();

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_RFI) {
			data = JSON.stringify({
				PmdCompCode: PMData.PmscrfiCompCode,
				PmdContactCode: userInfo.info.contactCode, //PMData.PmscrfiFromContactCode,
				PmdMpowerUser: userInfo.info.pmUser,
				PmdPartnCode: PMData.PmscrfiFromPartnCode,
				PmdPartnTypeCode: PMData.PmscrfiFromPartnTypeCode,
				PmdProjOraseq: PMData.PmscrfiProjOraseq,
				PmdStatusCode: null,
				PmdTitle: mailData.name,
				PmdTypeCode: 'ATTACHMENT',
				VUuid: uuid,
				PmdrVUuid: uuid,
			});
		}
		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE) {
			data = JSON.stringify({
				PmdCompCode: PMData.DmiCompCode,
				PmdContactCode: userInfo.info.contactCode, //PMData.DmiContactCode,
				PmdMpowerUser: userInfo.info.pmUser,
				PmdPartnCode: PMData.DmiPartnCode,
				PmdPartnTypeCode: PMData.DmiPartnTypeCode,
				PmdProjOraseq: PMData.DmiProjOraseq,
				PmdStatusCode: null,
				PmdTitle: mailData.name,
				PmdTypeCode: 'ATTACHMENT',
				VUuid: uuid,
				PmdrVUuid: uuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL) {
			data = JSON.stringify({
				PmdCompCode: PMData.PmjCompCode,
				PmdContactCode: userInfo.info.contactCode, //PMData.PmjSubmContactCode,
				PmdMpowerUser: userInfo.info.pmUser,
				PmdPartnCode: PMData.PmjSubmPartnCode,
				PmdPartnTypeCode: PMData.PmjSubmPartnTypeCode,
				PmdProjOraseq: PMData.PmjProjOraseq,
				PmdStatusCode: null,
				PmdTitle: mailData.name,
				PmdTypeCode: 'ATTACHMENT',
				VUuid: uuid,
				PmdrVUuid: uuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS) {
			data = JSON.stringify({
				PmdCompCode: PMData.PmsmCompCode,
				PmdContactCode: userInfo.info.contactCode, //PMData.PmsmRecFromContactCode,
				PmdMpowerUser: userInfo.info.pmUser,
				PmdPartnCode: PMData.PmsmRecFromPartnCode,
				PmdPartnTypeCode: PMData.PmsmRecFromPartnTypeCode,
				PmdProjOraseq: PMData.PmsmProjOraseq,
				PmdStatusCode: null,
				PmdTitle: mailData.name,
				PmdTypeCode: 'ATTACHMENT',
				VUuid: uuid,
				PmdrVUuid: uuid,
			});
		}
		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_PCI) {
			data = JSON.stringify({
				PmdCompCode: PMData.CmmCompCode,
				PmdContactCode: userInfo.info.contactCode,
				PmdMpowerUser: userInfo.info.pmUser,
				PmdPartnCode: PMData.CmmTypeName,
				PmdPartnTypeCode: PMData.CmmTypeCode,
				PmdProjOraseq: PMData.CmmProjOraseq,
				PmdStatusCode: null,
				PmdTitle: mailData.name,
				PmdTypeCode: 'ATTACHMENT',
				VUuid: uuid,
				PmdrVUuid: uuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE) {
			data = JSON.stringify({
				PmdCompCode: selectedProject.PmpCompCode,
				PmdContactCode: userInfo.info.contactCode,
				PmdPartnCode: userInfo.info.partnerCode,
				PmdPartnTypeCode: userInfo.info.partnerTypeCode,
				PmdProjOraseq: selectedProject.value,
				PmdRevDate: new Date(),
				PmdRevNum: 0,
				PmdStatusCode: PMData?.PmdStatusCode || null,
				PmdId: PMData?.PmdId || null,
				PmdTitle: mailData.name,
				PmdTypeCode: PMData.PmdgtTypeCode,
				VUuid: uuid,
				PmdrVUuid: uuid,
			});
		}
		const fileName = hashFileName(mailData.name);
		// Remove empty keys
		data = filterEmptyFields(data);

		let headers = {
			'Content-Type': 'application/vnd.oracle.adf.resourceitem+json',
		};

		if (userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth) {
			headers.Authorization = `Bearer ${userInfo?.accessToken}`;
		}

		let config = {
			method: 'post',
			maxBodyLength: Infinity,
			url: `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.documents}?filename=${fileName}`,

			headers: headers,
			...(userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth
				? {}
				: {
					auth: {
						username: userInfo.username,
						password: userInfo.password,
					},
				}),
			data: data,
		};

		axios
			.request(config)
			.then(response => {
				resolve(response.data);
			})
			.catch(err => {
				resolve(false);
				console.log('Document Creation Error', err);
			});
	});
};

export const uploadDocumentsToRecord = async (recordData, mailData, userInfo) => {
	return new Promise((resolve, reject) => {
		let data = new FormData();
		let blobData = downloadAttachmentLocally(mailData);
		const fileName = hashFileName(mailData.name);
		data.append('document-content-file', blobData, fileName);

		let headers = {};

		if (userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth) {
			headers.Authorization = `Bearer ${userInfo?.accessToken}`;
		}

		let config = {
			method: 'post',
			maxBodyLength: Infinity,
			url: `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.documents}/${recordData.VUuid}`,
			...(userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth
				? {}
				: {
					auth: {
						username: userInfo.username,
						password: userInfo.password,
					},
				}),
			headers: headers,
			data: data,
		};

		axios
			.request(config)
			.then(response => {
				if ((response.data.message = 'Document File Upload Success')) {
					resolve(response.data);
				} else {
					throw new Error('Upload failed!');
				}
			})
			.catch(err => {
				resolve(false);
			});
	});
};

export const attachUploadedDocumentToPMObject = (
	PMData,
	recordData,
	mailData,
	userInfo,
	setIsLoading,
	setAttachmentData,
) => {
	return new Promise((resolve, reject) => {
		let data = {};
		let url = '';
		let pmObjectOraseq = '';
		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_RFI) {
			((url = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.rfisv12}/${PMData.VUuid}/child/RelatedDocumentView`),
				(pmObjectOraseq = PMData.PmscrfiRfiOraseq));
			data = JSON.stringify({
				VUuid: uuidv4(),
				SysrdAttName: '',
				SysrdAttPath: mailData.name,
				SysrdContactCode: userInfo.info.contactCode,
				SysrdContactName: `${userInfo.info.firstName} ${userInfo.info.lastName}`, // SysrdContactName: PMData.PmscrfiFromContactName,
				SysrdDocId: recordData.PmdId,
				SysrdDocTitle: PMData.PmscrfiRfiId,
				SysrdDocTypeCode: 'ATTACHMENT',
				SysrdDocTypeName: 'Attachment',
				SysrdObjectType: CMIC_OBJECT_CODES.RFI_OBJECT_CODE,
				SysrdPartnCode: userInfo.info.partnerCode,
				SysrdPartnTypeCode: userInfo.info.partnerTypeCode,
				SysrdProjOraseq: PMData.PmscrfiProjOraseq,
				SysrdRevNum: 0,
				SysrdStatus: 'OPEN',
				SysrdDocVUuid: recordData.VUuid,
				SysrdObjectVUuid: PMData.VUuid,
				SysrdRevisionVUuid: recordData.DocRevisionView[0].VUuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE) {
			((url = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.issues}/${PMData.VUuid}/child/RelatedDocumentView`),
				(pmObjectOraseq = PMData.DmiProjOraseq));
			data = JSON.stringify({
				VUuid: uuidv4(),
				SysrdAttName: '',
				SysrdAttPath: mailData.name,
				SysrdContactCode: userInfo.info.contactCode,
				SysrdContactName: `${userInfo.info.firstName} ${userInfo.info.lastName}`, //  SysrdContactName: PMData.DmiContactName,
				SysrdDocId: recordData.PmdId,
				SysrdDocTitle: PMData.DmiIssueId,
				SysrdDocTypeCode: 'ATTACHMENT',
				SysrdDocTypeName: 'Attachment',
				SysrdObjectType: CMIC_OBJECT_CODES.ISSUE_OBJECT_CODE,
				SysrdPartnCode: userInfo.info.partnerCode,
				SysrdPartnTypeCode: userInfo.info.partnerTypeCode,
				SysrdProjOraseq: PMData.DmiProjOraseq,
				SysrdRevNum: 0,
				SysrdStatus: 'OPEN',
				SysrdDocVUuid: recordData.VUuid,
				SysrdObjectVUuid: PMData.VUuid,
				SysrdRevisionVUuid: recordData.DocRevisionView[0].VUuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL) {
			((url = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.journals}/${PMData.VUuid}/child/RelatedDocumentView`),
				(pmObjectOraseq = PMData.PmjProjOraseq));
			data = JSON.stringify({
				VUuid: uuidv4(),
				SysrdAttName: '',
				SysrdAttPath: mailData.name,
				SysrdContactCode: userInfo.info.contactCode,
				SysrdContactName: `${userInfo.info.firstName} ${userInfo.info.lastName}`, //  SysrdContactName: PMData.PmjPrepContactName,
				SysrdDocId: recordData.PmdId,
				SysrdDocTitle: PMData.PmjJournalId,
				SysrdDocTypeCode: 'ATTACHMENT',
				SysrdDocTypeName: 'Attachment',
				SysrdObjectType: CMIC_OBJECT_CODES.DAILY_JOURNAL_OBJECT_CODE,
				SysrdPartnCode: userInfo.info.partnerCode,
				SysrdPartnTypeCode: userInfo.info.partnerTypeCode,
				SysrdProjOraseq: PMData.PmjProjOraseq,
				SysrdRevNum: 0,
				SysrdStatus: 'OPEN',
				SysrdDocVUuid: recordData.VUuid,
				SysrdObjectVUuid: PMData.VUuid,
				SysrdRevisionVUuid: recordData.DocRevisionView[0].VUuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS) {
			((url = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.submittals}/${PMData.VUuid}/child/RelatedDocumentView`),
				(pmObjectOraseq = PMData.PmsmProjOraseq));
			data = JSON.stringify({
				VUuid: uuidv4(),
				SysrdAttName: '',
				SysrdAttPath: mailData.name,
				SysrdContactCode: userInfo.info.contactCode,
				SysrdContactName: `${userInfo.info.firstName} ${userInfo.info.lastName}`, // SysrdContactName: PMData.PmsmRecFromContactName,
				SysrdDocId: recordData.PmdId,
				SysrdDocTitle: PMData.PmsmSbmtId,
				SysrdDocTypeCode: 'ATTACHMENT',
				SysrdDocTypeName: 'Attachment',
				SysrdObjectType: CMIC_OBJECT_CODES.SUBMITTALS_OBJECT_CODE,
				SysrdPartnCode: userInfo.info.partnerCode,
				SysrdPartnTypeCode: userInfo.info.partnerTypeCode,
				SysrdProjOraseq: PMData.PmsmProjOraseq,
				SysrdRevNum: 0,
				SysrdStatus: 'OPEN',
				SysrdDocVUuid: recordData.VUuid,
				SysrdObjectVUuid: PMData.VUuid,
				SysrdRevisionVUuid: recordData.DocRevisionView[0].VUuid,
			});
		}

		if (PMData.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_PCI) {
			((url = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.pcis}/${PMData.VUuid}/child/RelatedDocumentView`),
				(pmObjectOraseq = PMData.PmscrfiRfiOraseq));
			data = JSON.stringify({
				VUuid: uuidv4(),
				SysrdAttName: '',
				SysrdAttPath: mailData.name,
				SysrdContactCode: userInfo.info.contactCode,
				SysrdContactName: `${userInfo.info.firstName} ${userInfo.info.lastName}`, // SysrdContactName: PMData.PmscrfiFromContactName,
				SysrdDocId: recordData.PmdId,
				SysrdDocTitle: PMData.CmmCode,
				SysrdDocTypeCode: 'ATTACHMENT',
				SysrdDocTypeName: 'Attachment',
				SysrdObjectType: CMIC_OBJECT_CODES.PCI_OBJECT_CODE,
				SysrdPartnCode: userInfo.info.partnerCode,
				SysrdPartnTypeCode: userInfo.info.partnerTypeCode,
				SysrdProjOraseq: PMData.CmmProjOraseq,
				SysrdRevNum: 0,
				SysrdStatus: 'OPEN',
				SysrdDocVUuid: recordData.VUuid,
				SysrdObjectVUuid: PMData.VUuid,
				SysrdRevisionVUuid: recordData.DocRevisionView[0].VUuid,
			});
		}

		let headers = {
			'Content-Type': 'application/vnd.oracle.adf.resourceitem+json',
		};

		if (userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth) {
			headers.Authorization = `Bearer ${userInfo?.accessToken}`;
		}

		let config = {
			method: 'post',
			maxBodyLength: Infinity,
			url: url,
			...(userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth
				? {}
				: {
					auth: {
						username: userInfo.username,
						password: userInfo.password,
					},
				}),
			headers: headers,
			data: data,
		};

		axios
			.request(config)
			.then(response => {
				resolve(true);

				setAttachmentData &&
					setAttachmentData(prev => {
						let newAttachedData = prev.map(data => {
							if (data.id === mailData.id) {
								return { ...mailData, isUploaded: true };
							} else return data;
						});
						return newAttachedData;
					});
			})
			.catch(err => {
				console.log(err);
				setIsLoading(false);
				resolve(false);
			});
	});
};
